/**
 * TournamentBracket -- SVG-based single-elimination bracket display.
 *
 * Renders a visual bracket with connecting lines, match boxes showing
 * player names and scores, round labels, and current user highlighting.
 * Responsive with horizontal scroll on mobile.
 */

export interface TournamentData {
  id: string;
  title: string;
  gameKey: string;
  gameThumbnail: string;
  format: 'highscore' | 'bracket';
  participantCount: number;
  maxParticipants: number;
  status: 'registration' | 'active' | 'completed';
  startDate: string;
  endDate: string;
  description: string;
  joined?: boolean;
  winnerName?: string;
}

export interface BracketMatch {
  id: string;
  round: number;
  position: number;
  player1: { name: string; score: number };
  player2: { name: string; score: number };
  winnerId?: string; // 'p1' or 'p2'
  completed: boolean;
}

// Layout constants
const MATCH_W = 180;
const MATCH_H = 60;
const MATCH_GAP_V = 24;
const ROUND_GAP_H = 60;
const ROUND_LABEL_H = 32;
const PADDING = 16;

function getRoundLabel(round: number, totalRounds: number, t: (key: string) => string): string {
  if (round === totalRounds) return t('community_bracket_final');
  if (round === totalRounds - 1) return t('community_bracket_semis');
  if (round === totalRounds - 2) return t('community_bracket_quarters');
  return `${t('community_bracket_round')} ${round}`;
}

export class TournamentBracket {
  render(
    container: HTMLElement,
    matches: BracketMatch[],
    t: (key: string, params?: Record<string, string | number>) => string,
    currentUserName?: string,
  ): void {
    if (matches.length === 0) {
      container.innerHTML = `<div class="p-6 text-center text-sm" style="color: var(--text-secondary)">${t('community_no_bracket_data')}</div>`;
      return;
    }

    // Determine rounds
    const totalRounds = Math.max(...matches.map((m) => m.round));
    const matchesByRound: Map<number, BracketMatch[]> = new Map();
    for (let r = 1; r <= totalRounds; r++) {
      const roundMatches = matches.filter((m) => m.round === r).sort((a, b) => a.position - b.position);
      matchesByRound.set(r, roundMatches);
    }

    // Calculate dimensions
    const maxMatchesInRound = Math.max(...Array.from(matchesByRound.values()).map((m) => m.length));
    const svgW = totalRounds * (MATCH_W + ROUND_GAP_H) - ROUND_GAP_H + PADDING * 2;
    const svgH = maxMatchesInRound * (MATCH_H + MATCH_GAP_V) - MATCH_GAP_V + ROUND_LABEL_H + PADDING * 2;

    let svgContent = '';

    // Draw round labels
    for (let r = 1; r <= totalRounds; r++) {
      const x = PADDING + (r - 1) * (MATCH_W + ROUND_GAP_H);
      svgContent += `<text x="${x + MATCH_W / 2}" y="${PADDING + 16}" text-anchor="middle" font-size="11" font-weight="700" fill="var(--text-secondary)">${getRoundLabel(r, totalRounds, t)}</text>`;
    }

    // Draw matches and connectors
    for (let r = 1; r <= totalRounds; r++) {
      const roundMatches = matchesByRound.get(r) || [];
      const roundMatchCount = roundMatches.length;

      // Center matches vertically relative to the first round
      const firstRoundCount = (matchesByRound.get(1) || []).length;
      const firstRoundHeight = firstRoundCount * (MATCH_H + MATCH_GAP_V) - MATCH_GAP_V;
      const currentRoundHeight = roundMatchCount * (MATCH_H + MATCH_GAP_V) - MATCH_GAP_V;
      const verticalOffset = (firstRoundHeight - currentRoundHeight) / 2;

      for (let i = 0; i < roundMatches.length; i++) {
        const match = roundMatches[i];
        const x = PADDING + (r - 1) * (MATCH_W + ROUND_GAP_H);
        const y = PADDING + ROUND_LABEL_H + verticalOffset + i * (MATCH_H + MATCH_GAP_V);

        const p1Highlight = currentUserName && match.player1.name === currentUserName;
        const p2Highlight = currentUserName && match.player2.name === currentUserName;
        const p1Winner = match.completed && match.winnerId === 'p1';
        const p2Winner = match.completed && match.winnerId === 'p2';

        // Match box background
        svgContent += `<rect x="${x}" y="${y}" width="${MATCH_W}" height="${MATCH_H}" rx="8" fill="var(--bg-secondary)" stroke="${(p1Highlight || p2Highlight) ? '#F59E0B' : 'var(--border)'}" stroke-width="${(p1Highlight || p2Highlight) ? 2 : 1}"/>`;

        // Divider line
        svgContent += `<line x1="${x}" y1="${y + MATCH_H / 2}" x2="${x + MATCH_W}" y2="${y + MATCH_H / 2}" stroke="var(--border)" stroke-width="1"/>`;

        // Player 1 (top half)
        const p1Color = p1Winner ? '#10B981' : p1Highlight ? '#F59E0B' : 'var(--text-primary)';
        const p1Weight = p1Winner ? '700' : '500';
        svgContent += `<text x="${x + 10}" y="${y + 20}" font-size="11" font-weight="${p1Weight}" fill="${p1Color}">${this.truncateName(match.player1.name, 14)}</text>`;
        if (match.player1.score > 0 || match.completed) {
          svgContent += `<text x="${x + MATCH_W - 10}" y="${y + 20}" text-anchor="end" font-size="11" font-weight="700" fill="${p1Color}">${match.player1.score}</text>`;
        }
        if (p1Winner) {
          svgContent += `<circle cx="${x + MATCH_W - 36}" cy="${y + 16}" r="3" fill="#10B981"/>`;
        }

        // Player 2 (bottom half)
        const p2Color = p2Winner ? '#10B981' : p2Highlight ? '#F59E0B' : 'var(--text-primary)';
        const p2Weight = p2Winner ? '700' : '500';
        svgContent += `<text x="${x + 10}" y="${y + MATCH_H - 10}" font-size="11" font-weight="${p2Weight}" fill="${p2Color}">${this.truncateName(match.player2.name, 14)}</text>`;
        if (match.player2.score > 0 || match.completed) {
          svgContent += `<text x="${x + MATCH_W - 10}" y="${y + MATCH_H - 10}" text-anchor="end" font-size="11" font-weight="700" fill="${p2Color}">${match.player2.score}</text>`;
        }
        if (p2Winner) {
          svgContent += `<circle cx="${x + MATCH_W - 36}" cy="${y + MATCH_H - 14}" r="3" fill="#10B981"/>`;
        }

        // Draw connector lines to next round
        if (r < totalRounds) {
          const nextRoundMatches = matchesByRound.get(r + 1) || [];
          const nextRoundCount = nextRoundMatches.length;
          const nextRoundHeight = nextRoundCount * (MATCH_H + MATCH_GAP_V) - MATCH_GAP_V;
          const nextVerticalOffset = (firstRoundHeight - nextRoundHeight) / 2;

          const nextMatchIdx = Math.floor(i / 2);
          const nextX = PADDING + r * (MATCH_W + ROUND_GAP_H);
          const nextY = PADDING + ROUND_LABEL_H + nextVerticalOffset + nextMatchIdx * (MATCH_H + MATCH_GAP_V);

          // From right edge of current match to left edge of next match
          const startX = x + MATCH_W;
          const startY = y + MATCH_H / 2;
          const endX = nextX;
          const endY = nextY + (i % 2 === 0 ? MATCH_H / 4 : (MATCH_H * 3) / 4);
          const midX = startX + ROUND_GAP_H / 2;

          svgContent += `<path d="M${startX} ${startY} H${midX} V${endY} H${endX}" fill="none" stroke="var(--text-secondary)" stroke-width="1.5" stroke-opacity="0.4"/>`;
        }
      }
    }

    container.innerHTML = `
      <div class="p-3 overflow-x-auto" style="-webkit-overflow-scrolling: touch">
        <svg width="${svgW}" height="${svgH}" viewBox="0 0 ${svgW} ${svgH}" xmlns="http://www.w3.org/2000/svg" style="min-width: ${svgW}px">
          ${svgContent}
        </svg>
      </div>
    `;
  }

  private truncateName(name: string, max: number): string {
    if (name.length <= max) return name;
    return name.slice(0, max - 1) + '\u2026';
  }
}
